import React, { Component } from "react";
import { connect } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import axios from "../../../axios-url";
import "./PrintJobs.scss";

// ============================================================================
// PrintJobs — reprint screen
//
// A KOT or bill that fails to print is NOT retried on its own. The server
// deliberately blocks a failed job from being re-enqueued (a permanent fault,
// such as a deleted printer, would otherwise insert a fresh row every three
// seconds forever), so recovery is a human decision — this screen.
//
// It opens on failures because that is what someone comes here to fix, but
// every filter can be widened: a captain who wants to hand a customer another
// copy of a bill that printed fine switches Status to Printed and reprints
// from there.
//
// Talks to the print server directly. That server is an IIS sub-application of
// this same site, so this is a same-origin call and needs no CORS.
// ============================================================================

const PRINT_API = "/RMSPrintServer/api/print";

// Mirrors PrintStatus in the server's PrintJob.cs. Cancelled (5) is omitted:
// nothing in the system sets it today, so offering it would only ever show an
// empty list.
const STATUS = {
  PENDING: 0,
  CLAIMED: 1,
  PRINTED: 2,
  FAILED: 3,
  SKIPPED: 4,
};

const STATUS_LABEL = {
  0: "Waiting",
  1: "Printing",
  2: "Printed",
  3: "Failed",
  4: "Skipped",
  5: "Cancelled",
};

// "Failed" is the default on purpose — see the header comment.
const STATUS_OPTIONS = [
  { label: "Failed", value: STATUS.FAILED },
  { label: "Waiting", value: STATUS.PENDING },
  { label: "Printing", value: STATUS.CLAIMED },
  { label: "Printed", value: STATUS.PRINTED },
  { label: "Skipped", value: STATUS.SKIPPED },
  { label: "All statuses", value: null },
];

const TYPE_OPTIONS = [
  { label: "KOT and Bill", value: null },
  { label: "KOT only", value: 1 },
  { label: "Bill only", value: 4 },
];

const HOURS_OPTIONS = [
  { label: "Last 6 hours", value: 6 },
  { label: "Today (24 hours)", value: 24 },
  { label: "Last 3 days", value: 72 },
  { label: "Last 7 days", value: 168 },
];

// The list changes without anyone touching it — a reprint moves from Waiting
// to Printed on its own — so it refreshes itself rather than leaving a captain
// looking at a stale screen and pressing Reprint a second time.
const AUTO_REFRESH_MS = 20000;

const formatTime = (value) => {
  if (!value) return "";
  // The API sends UTC without a trailing "Z". Left as-is, the browser reads it
  // as local time and every timestamp shifts by the UTC offset (5.5 hours in
  // IST), which would make tonight's failures look like they happened at dawn.
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
  const d = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

class PrintJobs extends Component {
  state = {
    jobs: [],
    loading: false,
    status: STATUS.FAILED,
    jobType: null,
    hours: 24,
    // PrintJobIds with a reprint in flight. Pressing Reprint queues real paper,
    // so the button for that row is disabled until the call comes back — a
    // double tap must not produce two tickets.
    reprinting: [],
  };

  componentDidMount() {
    this.load();
    this.timer = setInterval(this.load, AUTO_REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    this.cancelled = true;
  }

  load = async () => {
    const { status, jobType, hours } = this.state;

    // Status and JobType are omitted rather than sent as null: the server reads
    // an absent value as "no filter", and ASP.NET would bind the string "null"
    // to nothing useful.
    const params = { hours, limit: 200 };
    if (status !== null) params.status = status;
    if (jobType !== null) params.jobType = jobType;

    this.setState({ loading: true });
    try {
      const res = await axios.get(`${PRINT_API}/jobs`, { params });
      if (this.cancelled) return;
      this.setState({ jobs: res.data || [], loading: false });
    } catch (err) {
      if (this.cancelled) return;
      this.setState({ loading: false });
      this.toast &&
        this.toast.show({
          severity: "error",
          summary: "Could not load print jobs",
          detail: err.message,
        });
    }
  };

  setFilter = (patch) => this.setState(patch, this.load);

  reprint = async (job) => {
    this.setState((prev) => ({ reprinting: [...prev.reprinting, job.printJobId] }));

    try {
      await axios.post(`${PRINT_API}/reprint`, {
        printJobId: job.printJobId,
        requestedBy: this.props.loggedUserDetail?.userName || null,
      });

      this.toast &&
        this.toast.show({
          severity: "success",
          summary: "Sent to printer",
          detail: `${job.jobType === 4 ? "Bill" : "KOT"} queued for reprint`,
        });

      // A reprint is a NEW job row — the original stays Failed as a record of
      // what happened. Reloading is what brings the new row into the list.
      this.load();
    } catch (err) {
      this.toast &&
        this.toast.show({
          severity: "error",
          summary: "Reprint failed",
          detail: err.message,
        });
    } finally {
      this.setState((prev) => ({
        reprinting: prev.reprinting.filter((id) => id !== job.printJobId),
      }));
    }
  };

  // ── Column bodies ────────────────────────────────────────────────────────

  typeBody = (job) => (
    <span className={`pj-type pj-type--${job.jobType === 4 ? "bill" : "kot"}`}>
      {job.jobType === 4 ? "BILL" : "KOT"}
    </span>
  );

  ticketBody = (job) => job.billNumber || job.kotNumber || "—";

  statusBody = (job) => (
    <span className={`pj-status pj-status--${job.status}`}>
      {STATUS_LABEL[job.status] || job.status}
      {job.isReprint ? " (reprint)" : ""}
    </span>
  );

  attemptsBody = (job) => `${job.attempts} / ${job.maxAttempts}`;

  errorBody = (job) => <span className="pj-error">{job.lastError || ""}</span>;

  actionBody = (job) => {
    const busy = this.state.reprinting.includes(job.printJobId);
    return (
      <Button
        label={busy ? "Sending" : "Reprint"}
        icon={busy ? "pi pi-spin pi-spinner" : "pi pi-print"}
        className="p-button-sm"
        disabled={busy}
        onClick={() => this.reprint(job)}
      />
    );
  };

  render() {
    const { jobs, loading, status, jobType, hours } = this.state;

    return (
      <div className="pj-page">
        <Toast ref={(el) => (this.toast = el)} />

        <div className="pj-header">
          <h2 className="pj-title">Print jobs</h2>

          <div className="pj-filters">
            <Dropdown
              value={status}
              options={STATUS_OPTIONS}
              onChange={(e) => this.setFilter({ status: e.value })}
              placeholder="Status"
            />
            <Dropdown
              value={jobType}
              options={TYPE_OPTIONS}
              onChange={(e) => this.setFilter({ jobType: e.value })}
              placeholder="Type"
            />
            <Dropdown
              value={hours}
              options={HOURS_OPTIONS}
              onChange={(e) => this.setFilter({ hours: e.value })}
              placeholder="Period"
            />
            <Button
              label="Refresh"
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
              className="p-button-outlined p-button-sm"
              onClick={this.load}
              disabled={loading}
            />
          </div>
        </div>

        <DataTable
          value={jobs}
          dataKey="printJobId"
          paginator
          rows={15}
          responsive
          emptyMessage={
            status === STATUS.FAILED
              ? "No failed prints — everything got through."
              : "Nothing matches this filter."
          }
          className="pj-table"
        >
          <Column header="Time" body={(j) => formatTime(j.createdOn)} style={{ width: "10rem" }} />
          <Column header="Type" body={this.typeBody} style={{ width: "6rem" }} />
          <Column header="Ticket" body={this.ticketBody} style={{ width: "11rem" }} />
          <Column field="tableNumber" header="Table" style={{ width: "6rem" }} />
          <Column field="printerName" header="Printer" style={{ width: "12rem" }} />
          <Column header="Tries" body={this.attemptsBody} style={{ width: "6rem" }} />
          <Column header="Status" body={this.statusBody} style={{ width: "9rem" }} />
          <Column header="Reason" body={this.errorBody} />
          <Column body={this.actionBody} style={{ width: "9rem" }} />
        </DataTable>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(PrintJobs);
